import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BillingView } from '@lemoncloud/lemon-billings-api';
import { CardView } from '@lemoncloud/lemon-payments-api';
import { useWorkspaceService } from '@web/app/features/home/hooks/useWorkspaceService';
import { getWorkspaceBadge } from '@web/app/shared';
import { ChevronDown } from 'lucide-react';
import moment from 'moment';

import { getCurrencySymbol, getDollarByPrice, useBillingDate } from '@codes/react-utils';
import { DialogHeader, DialogTitle, Skeleton } from '@codes/ui-kit';

import { MAX_VISIBLE_COUNT } from '../../consts';

export const BillingsDetail = ({ billings, card }: { billings: BillingView[]; card: CardView | null }) => {
    const { t } = useTranslation();

    const [isExpanded, setIsExpanded] = useState(false);
    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const {
        myWorkspaces,
        myWorkspacesQueryResult: { isFetching: areMyWorkspaceFetching },
    } = useWorkspaceService();

    const isFetching = areMyWorkspaceFetching;

    const workspaceIds = Array.from(
        new Set(billings.map(billing => billing?.Product?.workspaceId).filter((id): id is string => Boolean(id)))
    );

    const filteredWorkspaces = useMemo(() => {
        if (!myWorkspaces || myWorkspaces.length === 0) {
            return [];
        }

        const workspaces = myWorkspaces.filter(workspace => workspaceIds.includes(workspace.id as string));

        if (isExpanded) {
            return workspaces;
        }

        const isExpandable = workspaces.length > MAX_VISIBLE_COUNT;

        if (isExpandable) {
            return workspaces.slice(0, MAX_VISIBLE_COUNT);
        }

        return workspaces;
    }, [billings, myWorkspaces, isExpanded, MAX_VISIBLE_COUNT, workspaceIds]);

    const getBillingsByWorkspaceId = (workspaceId: string | undefined) => {
        if (!workspaceId || !billings) {
            return [];
        }

        return billings.filter(billing => billing.Product?.workspaceId === workspaceId);
    };

    const getBillingsTotalPriceByWorkspaceId = (workspaceId: string | undefined) => {
        if (!billings || !workspaceId) {
            return [];
        }

        return billings
            .filter(billing => billing?.Product?.workspaceId === workspaceId)
            .map(billing => {
                if (billing.totalPrice$?.currency === 'USD') {
                    return billing.totalPrice;
                }

                return getDollarByPrice(billing.totalPrice || 0, billing.totalPrice$?.currency);
            })
            .reduce((prev, cur) => (prev || 0) + (cur || 0), 0)
            ?.toLocaleString();
    };

    const totalPrice = billings.reduce((cur, billing) => {
        if (billing.totalPrice$?.currency === 'USD') {
            return cur + (billing.totalPrice || 0);
        }

        return cur + getDollarByPrice(billing.totalPrice || 0, billing.totalPrice$?.currency);
    }, 0);

    const isExpandable = workspaceIds.length > MAX_VISIBLE_COUNT;

    const { estimatedBillingMs } = useBillingDate();

    const thisMonth = useMemo(() => {
        return moment(estimatedBillingMs).format('YYYY. MM. 05');
    }, [estimatedBillingMs]);

    const nextMonth = useMemo(() => {
        return moment(thisMonth).add(1, 'M').format('YYYY. MM. 05');
    }, [thisMonth]);

    return (
        <div className="h-full">
            <DialogHeader>
                <DialogTitle className="text-base">{t('myPage.billing.detail.headerName')}</DialogTitle>
            </DialogHeader>
            {isFetching ? (
                <div className="min-h-96 flex flex-col gap-y-4 p-[18px]">
                    <div className="flex justify-between">
                        <Skeleton className="w-full h-10" />
                    </div>
                    <div className="flex gap-x-4 items-center">
                        <Skeleton className="w-28 h-8 rounded" />
                        <Skeleton className="w-8 h-4 rounded" />
                    </div>
                    <div className="flex justify-between">
                        <Skeleton className="w-24 h-6" />
                        <Skeleton className="w-24 h-6" />
                    </div>
                    <div className="flex justify-between">
                        <Skeleton className="w-24 h-6" />
                        <Skeleton className="w-24 h-6" />
                    </div>
                    <Skeleton className="w-full h-1" />
                    <div className="flex justify-between">
                        <Skeleton className="w-full h-10" />
                    </div>
                    <div className="flex justify-between">
                        <Skeleton className="w-24 h-6" />
                        <Skeleton className="w-24 h-6" />
                    </div>
                    <div className="flex justify-between">
                        <Skeleton className="w-24 h-6" />
                        <Skeleton className="w-24 h-6" />
                    </div>
                </div>
            ) : (
                <div className="p-[18px] h-[calc(100%-52px)] overflow-auto flex flex-col">
                    {/* 이용내역 */}
                    <div className="text-lg font-medium mb-3 flex items-center justify-between">
                        <div>{t('myPage.usageDetails')}</div>
                        <div className="text-point">
                            {t('myPage.totalAmount')} ${totalPrice.toLocaleString()}
                        </div>
                    </div>
                    {filteredWorkspaces.map((workspace, idx) => (
                        <div className="flex flex-col gap-1" key={workspace.id}>
                            <div className="flex items-center gap-[3px] font-medium ">
                                {workspace?.name}
                                {getWorkspaceBadge(workspace.stereo)}
                            </div>
                            {getBillingsByWorkspaceId(workspace.id).map(billing => (
                                <div className="flex items-center justify-between text-sm" key={billing.id}>
                                    <div>{billing.name}</div>
                                    <div>
                                        $
                                        {getDollarByPrice(
                                            billing.totalPrice || 0,
                                            billing.totalPrice$?.currency
                                        )?.toLocaleString()}
                                        {billing.totalPrice$?.currency !== 'USD' &&
                                            ` / (${getCurrencySymbol(
                                                billing.currency
                                            )}${billing.totalPrice?.toLocaleString()})`}
                                    </div>
                                </div>
                            ))}
                            <div className="flex items-center justify-between text-sm font-semibold text-point mt-[2px]">
                                <div>{t('myPage.totalAmount')}</div>
                                <div>${getBillingsTotalPriceByWorkspaceId(workspace.id)}</div>
                            </div>
                            {idx < filteredWorkspaces.length - 1 && <div className="my-4 border-b border-divider" />}
                        </div>
                    ))}

                    {isExpandable && (
                        <div className="flex justify-center mt-[18px]">
                            <button
                                className="flex-1 text-xs text-sub-title flex justify-center items-center gap-1 font-medium"
                                onClick={toggleExpand}
                            >
                                {!isExpanded ? t('myPage.viewMore') : t('myPage.collapse')}
                                <ChevronDown className={`w-4 h-4 ${!isExpanded ? '' : 'rotate-180'} `} />
                            </button>
                        </div>
                    )}

                    <div className="my-4 border-b border-divider"></div>
                    {/* 결제정보 */}
                    <div className="text-lg font-medium mb-3">{t('myPage.paymentInformation')}</div>
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between text-sm">
                            <div>{t('myPage.billing.detail.scheduledPaymentDate')}</div>
                            <div>{thisMonth}</div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <div>{t('myPage.billing.detail.nextPaymentDate')}</div>
                            <div>{nextMonth}</div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <div>{t('myPage.billing.detail.paymentMethod')}</div>
                            <div>{card ? `${card.brandEn} (${card.last4}*)` : '-'}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
